import {nullify, ResponseData, roundToNumber, showDialog} from '@dvrd/dvr-controls';
import IDate, {MutateKey} from '@dvrd/idate';
import React, {MouseEventHandler, useEffect, useMemo, useState} from 'react';
import {ISO_DATE_FORMAT} from '../../../../util/constants';
import {PeriodLabel, SalesProductView} from '../../../../util/enums';
import {DataSplitMethod} from '../../../../util/interfaces';
import {getSalesDashboardProductV2} from '../../../../util/requestSender';
import {AggregatedSalesProduct, GeneralSalesProduct, SpecificSalesProduct} from '../../../../util/types';
import {createErrorMessage} from '../../../../util/utils';
import {getDatesFromPeriod} from '../../salesDashboardController';
import SalesProductStats from './salesProductStats';
import {useTranslation} from 'react-i18next';

function groupMethodFromPeriod(
    period: PeriodLabel,
    customStart: IDate | null,
    customEnd: IDate | null
): DataSplitMethod {
    switch (period) {
        case PeriodLabel.TODAY:
        case PeriodLabel.YESTERDAY:
            return DataSplitMethod.HOUR;
        case PeriodLabel.LAST_7_DAYS:
        case PeriodLabel.LAST_30_DAYS:
        case PeriodLabel.WEEK_TO_DATE:
        case PeriodLabel.LAST_WEEK:
        case PeriodLabel.MONTH_TO_DATE:
        case PeriodLabel.LAST_MONTH:
            return DataSplitMethod.DAY;
        case PeriodLabel.LAST_90_DAYS:
        case PeriodLabel.QUARTER_TO_DATE:
        case PeriodLabel.LAST_QUARTER:
        case PeriodLabel.YEAR_TO_DATE:
        case PeriodLabel.LAST_YEAR:
            return DataSplitMethod.WEEK;
        case PeriodLabel.ALL:
            return DataSplitMethod.MONTH;
        case PeriodLabel.CUSTOM: {
            if (!customStart || !customEnd) return DataSplitMethod.MONTH;
            const diff = customEnd.absDiff(customStart, 'days');
            if (diff <= 2) return DataSplitMethod.HOUR;
            else if (diff <= 30) return DataSplitMethod.DAY;
            else if (diff <= 90) return DataSplitMethod.WEEK;
            else return DataSplitMethod.MONTH;
        }
    }
}

/**
 * Fills gaps in the entries array by inserting zero-valued entries for each date interval
 * between startDate and endDate that has no existing data. This ensures the chart/table
 * shows a continuous timeline without missing periods. The result is sorted chronologically.
 */
function zeroFillEntries(
    entries: Array<SpecificSalesProduct>,
    startDate: IDate,
    endDate: IDate,
    groupMethod: DataSplitMethod
): Array<SpecificSalesProduct> {
    const emptyEntry: (_: IDate) => SpecificSalesProduct = (invoiceDate: IDate) => ({
        num_orders: 0,
        quantity: 0,
        revenue_excl: 0,
        revenue_incl: 0,
        acos: 0,
        ad_cost: 0,
        commission: 0,
        cpc: 0,
        costs: 0,
        margin: 0,
        profit: 0,
        organic_sales: 0,
        return_costs: 0,
        return_quantity: 0,
        group_date: invoiceDate,
        returned_commission: 0,
        returned_revenue_excl: 0,
        returned_vat_amount: 0,
        sales_14d: 0,
        sponsored_sales: 0,
        vat_amount: 0,
        vat_percentage: 0,
        comparison: null,
        stock: 0
    });
    const _entries: Array<SpecificSalesProduct> = entries.map((entry: SpecificSalesProduct) => ({
        ...entry,
        group_date: new IDate(entry.group_date)
    }));
    let start = new IDate(startDate);
    let granularity: MutateKey = 'day';
    let dateFormatter: (date: IDate) => string = (date: IDate) => date.format('YYYY-MM-DD');
    if (groupMethod === DataSplitMethod.HOUR) {
        granularity = 'hour';
        dateFormatter = (date: IDate) => date.format('YYYY-MM-DD HH');
    } else if (groupMethod === DataSplitMethod.WEEK) {
        granularity = 'week';
        dateFormatter = (date: IDate) => date.format('YYYY-MM-DD ') + date.week();
    } else if (groupMethod === DataSplitMethod.MONTH) {
        granularity = 'month';
        dateFormatter = (date: IDate) => date.format('YYYY-MM');
    } else if (groupMethod === DataSplitMethod.QUARTER) {
        granularity = 'quarter';
        dateFormatter = (date: IDate) => date.format('YYYY-Q');
    } else if (groupMethod === DataSplitMethod.YEAR) {
        granularity = 'year';
        dateFormatter = (date: IDate) => date.format('YYYY');
    }
    const existingKeys = new Set(
        _entries.map((entry: SpecificSalesProduct) => dateFormatter(new IDate(entry.group_date)))
    );
    while (start.isAtOrBefore(endDate, granularity)) {
        if (!existingKeys.has(dateFormatter(start))) _entries.push(emptyEntry(start));
        start = nextDate(start, groupMethod);
    }
    _entries.sort((a: SpecificSalesProduct, b: SpecificSalesProduct) =>
        new IDate(a.group_date).diff(new IDate(b.group_date), granularity)
    );
    return _entries;
}

function nextDate(date: IDate, groupMethod: DataSplitMethod): IDate {
    switch (groupMethod) {
        case DataSplitMethod.HOUR:
            return date.clone().add(1, 'hour');
        case DataSplitMethod.DAY:
            return date.clone().add(1, 'day');
        case DataSplitMethod.WEEK:
            return date.clone().add(1, 'week');
        case DataSplitMethod.MONTH:
            return date.clone().add(1, 'month');
        case DataSplitMethod.QUARTER:
            return date.clone().add(1, 'quarter');
        case DataSplitMethod.YEAR:
            return date.clone().add(1, 'year');
    }
}

function aggregateData(entries: Array<SpecificSalesProduct>): AggregatedSalesProduct {
    let periodRevenueExcl = 0;
    let periodRevenueIncl = 0;
    let periodRevenueOrganic = 0;
    let periodRevenueSponsored = 0;
    let periodQuantity = 0;
    let periodCosts = 0;
    let periodProfit = 0;
    let periodReturnedRevenue = 0;
    let periodReturnedQuantity = 0;
    let periodMargin = 0;
    let periodMarginItems = 0;
    for (const entry of entries) {
        periodRevenueExcl += entry.revenue_excl;
        periodRevenueIncl += entry.revenue_incl;
        periodRevenueOrganic += entry.organic_sales;
        periodRevenueSponsored += entry.sponsored_sales;
        periodQuantity += entry.quantity;
        periodCosts += entry.costs;
        periodProfit += entry.profit ?? 0;
        periodReturnedRevenue += entry.returned_revenue_excl;
        periodReturnedQuantity += entry.return_quantity;
        if (entry.margin !== 0) {
            periodMargin += entry.margin ?? 0;
            periodMarginItems += 1;
        }
    }
    return {
        periodRevenueExcl,
        periodRevenueIncl,
        periodQuantity,
        periodProfit,
        periodReturnedRevenue,
        periodReturnedQuantity,
        periodMargin: periodMarginItems ? roundToNumber(periodMargin / periodMarginItems, 1) : 0,
        periodCosts,
        periodRevenueOrganic,
        periodRevenueSponsored
    };
}

function cumulativeData(entries: Array<SpecificSalesProduct>): Array<SpecificSalesProduct> {
    let lastProduct: SpecificSalesProduct | null = null;
    const cloned = entries.map(entry => ({...entry}));
    for (const entry of cloned) {
        if (lastProduct) {
            entry.quantity += lastProduct.quantity;
            entry.revenue_excl += lastProduct.revenue_excl;
            entry.revenue_incl += lastProduct.revenue_incl;
            entry.vat_amount += lastProduct.vat_amount;
            entry.sales_14d += lastProduct.sales_14d;
            entry.organic_sales += lastProduct.organic_sales;
            entry.ad_cost += lastProduct.ad_cost;
            entry.sponsored_sales += lastProduct.sponsored_sales;
            entry.costs += lastProduct.costs;
            entry.return_costs += lastProduct.return_costs;
            entry.commission += lastProduct.commission;
            entry.return_quantity += lastProduct.return_quantity;
            entry.returned_revenue_excl += lastProduct.returned_revenue_excl;
            entry.returned_vat_amount += lastProduct.returned_vat_amount;
            entry.returned_commission += lastProduct.returned_commission;
            entry.profit = (entry.profit ?? 0) + (lastProduct.profit ?? 0);
        }
        lastProduct = entry;
    }
    return cloned;
}

interface Props {
    onClose: MouseEventHandler;
    onChangeView: (view: SalesProductView) => MouseEventHandler;
    product: GeneralSalesProduct | null;
    accountIDs: Set<string>;
    period: PeriodLabel;
    customPeriodStart: IDate | null;
    customPeriodEnd: IDate | null;
    countryCodes: string;
}

type LoadParams = {
    period?: PeriodLabel;
    customPeriodStart?: IDate | null;
    customPeriodEnd?: IDate | null;
};

export default function SalesProductStatsController(props: Props) {
    const {t} = useTranslation();
    const {product, accountIDs, onChangeView, countryCodes} = props;
    const [periodEntries, setPeriodEntries] = useState<Array<SpecificSalesProduct>>([]);
    const [comparingEntries, setComparingEntries] = useState<Array<SpecificSalesProduct>>([]);
    const [cumulativeGraphs, setCumulativeGraphs] = useState<Set<string>>(new Set(['revenue', 'profit']));
    const [period, setPeriod] = useState<PeriodLabel>(props.period);
    const [customPeriodStart, setCustomPeriodStart] = useState<IDate | null>(props.customPeriodStart);
    const [customPeriodEnd, setCustomPeriodEnd] = useState<IDate | null>(props.customPeriodEnd);
    const [loading, setLoading] = useState(false);
    const [aggregatedPeriod, cumulativeEntries] = useMemo(
        () => [aggregateData(periodEntries), cumulativeData(periodEntries)],
        [periodEntries]
    );
    const aggregatedComparison = useMemo(() => aggregateData(comparingEntries), [comparingEntries]);

    function onChangeCumulativeGraphs(key: string) {
        return function (value: boolean) {
            const _cumulativeGraphs = new Set(cumulativeGraphs);
            if (value) _cumulativeGraphs.add(key);
            else _cumulativeGraphs.delete(key);
            setCumulativeGraphs(_cumulativeGraphs);
        };
    }

    function onChangePeriod(range: [IDate, IDate], period: PeriodLabel | null) {
        if (!period) period = PeriodLabel.CUSTOM;
        const customStart = range[0];
        const customEnd = range[1];
        setPeriod(period);
        if (period === PeriodLabel.CUSTOM) {
            setCustomPeriodStart(customStart);
            setCustomPeriodEnd(customEnd);
        }
        loadPeriodEntries({period, customPeriodStart: customStart, customPeriodEnd: customEnd});
    }

    function onNavigateBol() {
        if (!product?.global_id) return;
        const url = `https://www.bol.com/nl/nl/p/${product.title?.replace('/', '-')}/${product.global_id}`;
        window.open(url, '_blank');
    }

    function loadPeriodEntries(params?: LoadParams) {
        setLoading(true);
        const _period = params?.period ?? period;
        const customStart = params?.customPeriodStart ?? customPeriodStart;
        const customEnd = params?.customPeriodEnd ?? customPeriodEnd;
        const groupMethod = groupMethodFromPeriod(_period, customStart, customEnd);
        const [startDate, endDate] = getDatesFromPeriod(_period, customStart, customEnd);
        if (!startDate || !endDate) return;
        getSalesDashboardProductV2({
            data: {
                ean: nullify(product?.ean),
                account_ids: Array.from(accountIDs),
                start_date: startDate.format(ISO_DATE_FORMAT),
                end_date: endDate.format(ISO_DATE_FORMAT),
                group_method: groupMethod,
                country_codes: nullify(Array.from(countryCodes.split(',')))
            },
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    setPeriodEntries(zeroFillEntries(data.period_entries, startDate, endDate, groupMethod));
                    const comparingStartDate = new IDate(data.comparing_start_date);
                    const comparingEndDate = new IDate(data.comparing_end_date);
                    setComparingEntries(
                        zeroFillEntries(data.comparing_entries, comparingStartDate, comparingEndDate, groupMethod)
                    );
                    if (data.global_id && product) product.global_id = data.global_id;
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'salesDashboardV2.productDetails.salesProductStats.errorLoadProduct',
                                    'Het laden van het product is niet gelukt.'
                                )
                        ),
                        t('salesDashboardV2.productDetails.salesProductStats.loadFailed', 'Laden mislukt')
                    );
            }
        });
    }

    useEffect(() => {
        setPeriod(props.period);
        setCustomPeriodStart(props.customPeriodStart);
        setCustomPeriodEnd(props.customPeriodEnd);
        loadPeriodEntries({
            period: props.period,
            customPeriodStart: props.customPeriodStart,
            customPeriodEnd: props.customPeriodEnd
        });
    }, [product]);

    return (
        <SalesProductStats
            onChangePeriod={onChangePeriod}
            onChangeView={onChangeView}
            onNavigateBol={onNavigateBol}
            onChangeCumulativeGraphs={onChangeCumulativeGraphs}
            entries={periodEntries}
            period={period}
            customPeriodStart={customPeriodStart}
            customPeriodEnd={customPeriodEnd}
            groupMethod={groupMethodFromPeriod(period, customPeriodStart, customPeriodEnd)}
            loading={loading}
            product={product}
            aggregatedPeriod={aggregatedPeriod}
            aggregatedComparison={aggregatedComparison}
            cumulativeEntries={cumulativeEntries}
            cumulativeGraphs={cumulativeGraphs}
        />
    );
}
